import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
} from 'react-router';

import { Button } from '~/components/ui/button';
import { SessionWatcher } from '~/features/auth/session-watcher';
import { APP_NAME } from '~/lib/config';
import { initErrorReporting, reportError } from '~/lib/monitoring';
import { AppProviders } from '~/providers/app-providers';
import type { Route } from './+types/root';
import './styles/app.css';

// At module scope so a crash during the first render is already reportable;
// waiting for an effect would miss exactly the errors that break the app before
// anything is on screen.
initErrorReporting();

export const links: Route.LinksFunction = () => [
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000&family=DM+Mono:wght@400;500&display=swap',
  },
  { rel: 'icon', href: '/favicon.svg', type: 'image/svg+xml' },
  // iOS takes the Home Screen icon from here and not from the manifest, and it
  // wants an opaque square: transparency is composited onto black, and the
  // rounded corners are applied by the OS.
  { rel: 'apple-touch-icon', href: '/apple-touch-icon.png' },
  // Installing to the Home Screen is what unlocks push on iOS at all, and the
  // manifest's `display: standalone` is what makes the install offer appear.
  { rel: 'manifest', href: '/manifest.webmanifest' },
];

export const meta: Route.MetaFunction = () => [
  { title: APP_NAME },
  { name: 'description', content: 'Internal operations hub for Epic Manufacturing.' },
];

/**
 * The document shell. Providers live here rather than in `App` so that the
 * ErrorBoundary below renders with theming and toasts available too.
 *
 * `suppressHydrationWarning` on <html> is required by next-themes: it writes the
 * theme class onto the element before React hydrates, which is the entire point
 * (no flash of the wrong theme) and by definition a hydration mismatch.
 */
export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {/*
          Part numbers like "395-25206-000" look enough like phone numbers that
          iOS Safari turns them into tel: links. Explicit <a href="tel:"> links
          still work; only the guessing is switched off.
        */}
        <meta name="format-detection" content="telephone=no" />
        <Meta />
        <Links />
      </head>
      <body className="min-h-svh antialiased">
        <AppProviders>{children}</AppProviders>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  return (
    <>
      <SessionWatcher />
      <Outlet />
    </>
  );
}

/**
 * Shown while the route guards resolve the session on a cold load. Required in
 * SPA mode: there is no server render, so something has to occupy the screen
 * while `clientLoader` runs.
 */
export function HydrateFallback() {
  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-4">
      <div className="size-8 animate-spin rounded-full border-2 border-muted border-t-foreground" />
      <p className="text-sm text-muted-foreground">Loading {APP_NAME}…</p>
    </div>
  );
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  // Everything that gets this far is a crash somebody saw. A 404 is the router
  // working, so it is not worth an issue.
  if (!isRouteErrorResponse(error)) {
    reportError(error);
  }

  let title = 'Something went wrong';
  let detail = 'An unexpected error occurred. Try again, or head back to the dashboard.';
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    title = error.status === 404 ? 'Page not found' : `Error ${error.status}`;
    detail =
      error.status === 404
        ? 'That page does not exist, or you do not have access to it.'
        : error.statusText || detail;
  } else if (import.meta.env.DEV && error instanceof Error) {
    detail = error.message;
    stack = error.stack;
  }

  return (
    <main className="mx-auto flex min-h-svh max-w-2xl flex-col items-center justify-center gap-4 p-6 text-center">
      <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
      <p className="text-muted-foreground">{detail}</p>
      <Button asChild>
        <a href="/">Back to dashboard</a>
      </Button>
      {stack && (
        <pre className="mt-4 w-full overflow-x-auto rounded-lg bg-muted p-4 text-left text-xs">
          <code>{stack}</code>
        </pre>
      )}
    </main>
  );
}
